// Hand-editable draft editor (right pane) for the verbal-authoring
// modal. Renders the recipe meta (code, name, tags) and the lines list
// with the matched/pending dot indicators. Every field is editable in
// place; chat-driven refinement is a separate left-pane component
// (Phase 5) that produces new drafts the editor re-renders.
//
// Each line shows an inline material picker:
//   - pending lines render the searchable picker open, seeded with the
//     heard name, so the operator can resolve the match before saving.
//   - matched lines show the canonical material with a small "Change"
//     affordance that re-opens the picker for swaps.
//
// Plan reference: `docs/planning/mfg_recipe_verbal_authoring.md` §3.4.

import { useCallback, useEffect, useRef, useState } from "react";
import { mfgMdInputClass, mfgMdSelectClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { parseRecipeTagsInput, formatRecipeTagsInput } from "@/lib/manufacturing/recipeTagsInput";
import type { DictatedDraftLine, DictatedRecipeDraft } from "@/lib/manufacturing/types/recipeDictation";
import type { MfgMaterial, MfgRecipeLineRole } from "@/lib/manufacturing/types";
import { MaterialSearchableSelect } from "./MaterialSearchableSelect";
import { SlotLabelCombobox } from "./SlotLabelCombobox";

const ROLE_OPTIONS: readonly { value: MfgRecipeLineRole; label: string }[] = [
  { value: "structural", label: "Structural" },
  { value: "standard_option", label: "Standard option" },
  { value: "upgrade_option", label: "Upgrade option" },
];

export function ManufacturingDictatedRecipeDraftEditor({
  draft,
  onChange,
  materials,
  orgSlotLabels = [],
}: {
  draft: DictatedRecipeDraft;
  onChange: (next: DictatedRecipeDraft) => void;
  /** Active material catalog. Passed straight through to each line so
   *  the inline picker can resolve / change matches without an extra
   *  round-trip. */
  materials: ReadonlyArray<MfgMaterial>;
  /** Org slot labels for datalist suggestions on each line (no chip flood). */
  orgSlotLabels?: readonly string[];
}) {
  const [tagsInput, setTagsInput] = useState(() => formatRecipeTagsInput(draft.tags));
  const tagsFocusedRef = useRef(false);
  const draftTagsKey = draft.tags.join("\0");

  // Sync when the draft's tags change externally (AI refine / re-extract).
  useEffect(() => {
    if (tagsFocusedRef.current) return;
    setTagsInput(formatRecipeTagsInput(draft.tags));
  }, [draftTagsKey, draft.tags]);

  const setMeta = useCallback(
    (patch: Partial<Pick<DictatedRecipeDraft, "code" | "name">>) => {
      onChange({ ...draft, ...patch });
    },
    [draft, onChange]
  );

  const commitTagsInput = useCallback(
    (text: string) => {
      onChange({ ...draft, tags: parseRecipeTagsInput(text) });
    },
    [draft, onChange]
  );

  const setLine = useCallback(
    (idx: number, patch: Partial<DictatedDraftLine>) => {
      const next = draft.lines.slice();
      next[idx] = { ...next[idx], ...patch };
      onChange({ ...draft, lines: next });
    },
    [draft, onChange]
  );

  const removeLine = useCallback(
    (idx: number) => {
      const next = draft.lines.slice();
      next.splice(idx, 1);
      onChange({ ...draft, lines: next });
    },
    [draft, onChange]
  );

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Code
          <input
            type="text"
            className={`${mfgMdInputClass} mt-1`}
            value={draft.code}
            onChange={(e) => setMeta({ code: e.target.value })}
            placeholder="e.g. 8X10-LOFT"
          />
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Name
          <input
            type="text"
            className={`${mfgMdInputClass} mt-1`}
            value={draft.name}
            onChange={(e) => setMeta({ name: e.target.value })}
            placeholder="e.g. 8x10 Lofted Barn"
          />
        </label>
      </div>

      <label className="block text-xs text-gray-600 dark:text-zinc-300">
        Tags (comma-separated)
        <input
          type="text"
          className={`${mfgMdInputClass} mt-1`}
          value={tagsInput}
          onChange={(e) => setTagsInput(e.target.value)}
          onFocus={() => {
            tagsFocusedRef.current = true;
          }}
          onBlur={(e) => {
            tagsFocusedRef.current = false;
            commitTagsInput(e.target.value);
          }}
          placeholder="wood, lofted, premium"
          autoComplete="off"
        />
      </label>

      <div>
        <div className="mb-1 flex items-center justify-between">
          <h4 className="text-xs font-semibold text-gray-700 dark:text-zinc-300">
            Lines ({draft.lines.length})
          </h4>
          <div className="text-[11px] text-gray-500 dark:text-zinc-400">
            Match each line before saving
          </div>
        </div>
        {draft.lines.length === 0 ? (
          <div className="rounded-md border border-dashed border-gray-200 px-3 py-4 text-center text-xs text-gray-500 dark:border-zinc-700 dark:text-zinc-400">
            No lines yet — use chat refinement to add them.
          </div>
        ) : (
          <ul className="space-y-2">
            {draft.lines.map((line, idx) => (
              <li
                key={idx}
                className="rounded-md border border-gray-200 bg-white p-2 dark:border-zinc-700 dark:bg-zinc-900/40"
              >
                <LineRow
                  line={line}
                  materials={materials}
                  orgSlotLabels={orgSlotLabels}
                  onChange={(patch) => setLine(idx, patch)}
                  onRemove={() => removeLine(idx)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function LineRow({
  line,
  materials,
  orgSlotLabels,
  onChange,
  onRemove,
}: {
  line: DictatedDraftLine;
  materials: ReadonlyArray<MfgMaterial>;
  orgSlotLabels: readonly string[];
  onChange: (patch: Partial<DictatedDraftLine>) => void;
  onRemove: () => void;
}) {
  const matched = line.match.kind === "matched";
  // For pending lines the picker is always open (the operator MUST
  // resolve the match before save). For matched lines it stays closed
  // unless the operator explicitly clicks "Change". The derivation
  // keeps the visibility in sync if a chat-refine round-trip flips
  // the line back to pending, where naive local state would lag.
  const [changeRequested, setChangeRequested] = useState<boolean>(false);
  const showPicker = !matched || changeRequested;

  const handlePick = (m: MfgMaterial) => {
    onChange({
      match: { kind: "matched", material_id: m.id, canonical_name: m.name },
      material_name_heard: line.material_name_heard || m.name,
    });
    setChangeRequested(false);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className={`inline-block h-2 w-2 shrink-0 rounded-full ${matched ? "bg-emerald-500" : "bg-amber-500"}`}
            aria-hidden="true"
          />
          <div className="min-w-0">
            {matched && line.match.kind === "matched" ? (
              <>
                <div className="truncate text-sm font-medium text-gray-900 dark:text-zinc-100">
                  {line.match.canonical_name}
                </div>
                {line.material_name_heard &&
                line.material_name_heard !== line.match.canonical_name ? (
                  <div className="truncate text-[11px] text-gray-500 dark:text-zinc-400">
                    heard: "{line.material_name_heard}"
                  </div>
                ) : null}
              </>
            ) : (
              <div className="truncate text-sm font-medium text-amber-800 dark:text-amber-300">
                Unmatched — pick a material below
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {matched ? (
            <button
              type="button"
              className="text-[11px] text-indigo-700 underline hover:text-indigo-800 dark:text-indigo-300"
              onClick={() => setChangeRequested((v) => !v)}
            >
              {changeRequested ? "Cancel" : "Change"}
            </button>
          ) : null}
          <button
            type="button"
            className="text-[11px] text-red-700 underline hover:text-red-800 dark:text-red-400"
            onClick={onRemove}
          >
            Remove
          </button>
        </div>
      </div>

      {showPicker ? (
        <MaterialSearchableSelect
          materials={materials}
          initialQuery={line.material_name_heard}
          excludeId={line.match.kind === "matched" ? line.match.material_id : null}
          onSelect={handlePick}
          placeholder="Search materials by code or name…"
        />
      ) : null}

      <div className="grid grid-cols-3 gap-2">
        <label className="block text-[11px] text-gray-600 dark:text-zinc-300">
          Qty
          <input
            type="number"
            min="0"
            step="any"
            className={`${mfgMdInputClass} mt-1`}
            value={Number.isFinite(line.quantity) ? line.quantity : 0}
            onChange={(e) => onChange({ quantity: Number(e.target.value) || 0 })}
          />
        </label>
        <label className="block text-[11px] text-gray-600 dark:text-zinc-300">
          Role
          <select
            className={`${mfgMdSelectClass} mt-1 w-full`}
            value={line.role}
            onChange={(e) => onChange({ role: e.target.value as MfgRecipeLineRole })}
          >
            {ROLE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-[11px] text-gray-600 dark:text-zinc-300">
          Slot
          <div className="mt-1">
            <SlotLabelCombobox
              value={line.slot_label}
              suggestions={orgSlotLabels}
              placeholder="e.g. joist"
              className={`${mfgMdInputClass} w-full`}
              onChange={(slot_label) => onChange({ slot_label })}
            />
          </div>
        </label>
      </div>

      {line.role === "upgrade_option" ? (
        <label className="block text-[11px] text-gray-600 dark:text-zinc-300">
          Upgrade price delta
          <input
            type="number"
            min="0"
            step="any"
            className={`${mfgMdInputClass} mt-1`}
            value={line.upgrade_price_delta ?? ""}
            onChange={(e) => {
              const n = Number(e.target.value);
              onChange({
                upgrade_price_delta: Number.isFinite(n) && n >= 0 ? n : null,
              });
            }}
            placeholder="0"
          />
        </label>
      ) : null}
    </div>
  );
}
